/**
 * Central playground state machine.
 *
 * Owns: sessions index, active session, message list, streaming
 * lifecycle, governance trace updates, and localStorage persistence.
 *
 * The page renders four panels (icon rail, sessions, chat, governance)
 * each consuming a slice of this hook — no other component reaches
 * straight into ``localStorage`` or the SSE client.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ContentFrame,
  DoneFrame,
  ErrorFrame,
  GovernanceFrame,
  PlaygroundChatHandle,
  PlaygroundChatMessage,
  streamPlaygroundChat,
} from "./sse";
import {
  createBlankSession,
  deleteSession as deleteSessionStorage,
  deriveTitle,
  loadActiveSessionId,
  loadGovPanelOpen,
  loadSession,
  loadSessionIndex,
  newMessageId,
  saveActiveSessionId,
  saveGovPanelOpen,
  saveSession,
  saveSessionIndex,
  utf8ByteLength,
  MAX_MESSAGE_BYTES,
} from "./storage";
import type {
  GovernanceStep,
  GovernanceStepId,
  PlaygroundMessage,
  PlaygroundSession,
} from "./types";

const STEP_ORDER: GovernanceStepId[] = [
  "policy",
  "pii",
  "routing",
  "provider",
  "audit",
];

const STEP_LABELS: Record<GovernanceStepId, string> = {
  policy: "Policy Check",
  pii: "PII Detection",
  routing: "Routing",
  provider: "Provider Call",
  audit: "Audit Log",
};

function initialSteps(): GovernanceStep[] {
  return STEP_ORDER.map((id) => ({
    id,
    meta: "pending",
    status: "pending",
    ms: 0,
    detail: null,
  }));
}

/**
 * Hydrate every persisted session, sorted by ``updatedAt`` desc.
 * Backed entries that fail to parse are silently dropped — they survive
 * in the underlying storage but won't crash the page.
 */
function hydrateSessions(): {
  sessions: Record<string, PlaygroundSession>;
  index: string[];
} {
  const index = loadSessionIndex();
  const sessions: Record<string, PlaygroundSession> = {};
  for (const id of index) {
    const session = loadSession(id);
    if (session) sessions[id] = session;
  }
  // Drop dead refs from the index so we don't show ghost rows.
  const cleanedIndex = Object.keys(sessions).sort((a, b) =>
    sessions[b].updatedAt.localeCompare(sessions[a].updatedAt)
  );
  if (cleanedIndex.length !== index.length) saveSessionIndex(cleanedIndex);
  return { sessions, index: cleanedIndex };
}

function buildChatMessages(
  session: PlaygroundSession,
  pendingUser: PlaygroundMessage
): PlaygroundChatMessage[] {
  const out: PlaygroundChatMessage[] = [];
  // When a skill is selected, the backend injects the skill version's
  // system prompt before the governance pipeline. The FE deliberately
  // skips its own system message so the prompt isn't double-applied —
  // the locked system prompt card on the FE is purely a preview.
  const hasSkill = session.skillRef !== null;
  if (!hasSkill && session.systemPrompt.trim().length > 0) {
    out.push({ role: "system", content: session.systemPrompt });
  }
  for (const msg of session.messages) {
    if (msg.role === "system") continue;
    if (msg.error) continue;
    if (!msg.content) continue;
    out.push({ role: msg.role, content: msg.content });
  }
  out.push({ role: pendingUser.role, content: pendingUser.content });
  return out;
}

export interface UsePlayground {
  sessions: Record<string, PlaygroundSession>;
  sessionIndex: string[];
  activeSessionId: string | null;
  activeSession: PlaygroundSession | null;
  /** Whether a stream is currently in flight for the active session. */
  isStreaming: boolean;
  /** ``true`` once the persisted sessions have been hydrated. */
  hydrated: boolean;
  /** Persistent: governance panel expanded / collapsed. */
  govPanelOpen: boolean;
  /** Message id currently bound to the governance panel (or the streaming one). */
  boundMessageId: string | null;
  /** Convenience accessor for the bound message's governance trace. */
  boundGovernance: PlaygroundMessage | null;

  // Session ops
  newSession(): PlaygroundSession;
  setActiveSession(id: string): void;
  deleteSession(id: string): void;
  updateSession(id: string, patch: Partial<PlaygroundSession>): void;

  // Composer
  send(content: string): Promise<void>;
  abort(): void;

  // UI
  toggleGovPanel(): void;
  bindMessage(messageId: string | null): void;
}

export function usePlayground(): UsePlayground {
  const [sessions, setSessions] = useState<Record<string, PlaygroundSession>>({});
  const [sessionIndex, setSessionIndex] = useState<string[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [govPanelOpen, setGovPanelOpen] = useState(true);
  const [boundMessageId, setBoundMessageId] = useState<string | null>(null);

  const streamRef = useRef<PlaygroundChatHandle | null>(null);

  // ----- Hydrate from localStorage -----
  useEffect(() => {
    const { sessions: bag, index } = hydrateSessions();
    setSessions(bag);
    setSessionIndex(index);
    const remembered = loadActiveSessionId();
    setActiveSessionId(
      remembered && bag[remembered] ? remembered : index[0] ?? null
    );
    setGovPanelOpen(loadGovPanelOpen());
    setHydrated(true);
  }, []);

  // ----- Storage write-through -----
  const persistSession = useCallback(
    (next: PlaygroundSession, nextIndex: string[]) => {
      const { evicted } = saveSession(next, nextIndex);
      if (evicted.length > 0) {
        setSessions((prev) => {
          const out = { ...prev };
          for (const id of evicted) delete out[id];
          return out;
        });
        setSessionIndex((prev) => prev.filter((id) => !evicted.includes(id)));
      }
    },
    []
  );

  const writeSession = useCallback(
    (session: PlaygroundSession) => {
      setSessions((prev) => ({ ...prev, [session.id]: session }));
      setSessionIndex((prev) => {
        const filtered = prev.filter((id) => id !== session.id);
        const nextIndex = [session.id, ...filtered];
        persistSession(session, nextIndex);
        return nextIndex;
      });
    },
    [persistSession]
  );

  const updateSession = useCallback(
    (id: string, patch: Partial<PlaygroundSession>) => {
      setSessions((prev) => {
        const current = prev[id];
        if (!current) return prev;
        const next: PlaygroundSession = {
          ...current,
          ...patch,
          updatedAt: new Date().toISOString(),
        };
        // No re-ordering of the index for metadata-only edits — only
        // ``send`` bumps the recent-first order.
        saveSession(next, sessionIndex);
        return { ...prev, [id]: next };
      });
    },
    [sessionIndex]
  );

  // ----- Session lifecycle -----
  const newSession = useCallback((): PlaygroundSession => {
    // No default model — the operator picks one from the catalog
    // (which already respects tenant policy). Pre-filling a model
    // that the tenant has blocked would just produce
    // ``ModelNotAllowedError`` on first send without the user ever
    // having an explicit choice to point at.
    const session = createBlankSession();
    writeSession(session);
    setActiveSessionId(session.id);
    saveActiveSessionId(session.id);
    setBoundMessageId(null);
    return session;
  }, [writeSession]);

  const setActiveSession = useCallback((id: string) => {
    setActiveSessionId(id);
    saveActiveSessionId(id);
    setBoundMessageId(null);
  }, []);

  const deleteSession = useCallback(
    (id: string) => {
      setSessions((prev) => {
        const out = { ...prev };
        delete out[id];
        return out;
      });
      setSessionIndex((prev) => {
        const next = prev.filter((sid) => sid !== id);
        saveSessionIndex(next);
        return next;
      });
      deleteSessionStorage(id);
      if (activeSessionId === id) {
        const fallback = sessionIndex.find((sid) => sid !== id) ?? null;
        setActiveSessionId(fallback);
        saveActiveSessionId(fallback);
      }
    },
    [activeSessionId, sessionIndex]
  );

  // ----- Bound governance message -----
  const bindMessage = useCallback((messageId: string | null) => {
    setBoundMessageId(messageId);
  }, []);

  // ----- Composer / streaming -----
  const send = useCallback(
    async (content: string) => {
      if (!activeSessionId) return;
      const trimmed = content.trim();
      if (!trimmed) return;
      if (utf8ByteLength(trimmed) > MAX_MESSAGE_BYTES) {
        return;
      }
      const session = sessions[activeSessionId];
      if (!session) return;
      // Send is gated by the composer (button disabled when model is
      // ``null``), but defensive narrow here so the SSE request shape
      // stays ``string`` and a stray caller cannot accidentally fire a
      // model-less request that would 4xx at the gateway.
      if (!session.model) return;
      const sessionModel: string = session.model;

      const now = new Date().toISOString();
      const userMsg: PlaygroundMessage = {
        id: newMessageId(),
        role: "user",
        content: trimmed,
        createdAt: now,
      };
      const assistantMsg: PlaygroundMessage = {
        id: newMessageId(),
        role: "assistant",
        content: "",
        createdAt: now,
        governance: {
          reqId: "",
          auditId: null,
          totalMs: 0,
          steps: initialSteps(),
        },
      };

      const wireMessages = buildChatMessages(session, userMsg);

      const nextSession: PlaygroundSession = {
        ...session,
        title:
          session.messages.length === 0 ? deriveTitle(userMsg) : session.title,
        updatedAt: now,
        messages: [...session.messages, userMsg, assistantMsg],
      };
      writeSession(nextSession);
      setBoundMessageId(assistantMsg.id);
      setIsStreaming(true);

      const onGovernance = (frame: GovernanceFrame) => {
        setSessions((prev) => {
          const target = prev[nextSession.id];
          if (!target) return prev;
          const updatedMessages = target.messages.map((m) => {
            if (m.id !== assistantMsg.id || !m.governance) return m;
            const stepIndex = m.governance.steps.findIndex(
              (s) => s.id === frame.step
            );
            const nextStep: GovernanceStep = {
              id: frame.step,
              meta: frame.meta,
              status: frame.status,
              ms: frame.ms,
              detail: frame.detail ?? null,
            };
            const steps =
              stepIndex >= 0
                ? m.governance.steps.map((s, i) =>
                    i === stepIndex ? nextStep : s
                  )
                : [...m.governance.steps, nextStep];

            // PII step → mirror types onto the message for the bubble badge.
            const updates: Partial<PlaygroundMessage> = { governance: { ...m.governance, steps } };
            if (frame.step === "pii" && frame.status === "flagged") {
              const types = Array.isArray(frame.detail?.types)
                ? (frame.detail!.types as string[])
                : [];
              const userIndex = target.messages.findIndex(
                (entry) => entry.id === userMsg.id
              );
              if (userIndex >= 0) {
                // Mutate the user message in place; we reassign below.
                const usr = target.messages[userIndex];
                target.messages[userIndex] = {
                  ...usr,
                  redactedTypes: types,
                };
              }
            }
            if (frame.status === "blocked") {
              updates.flagged = frame.step === "pii" ? "pii" : "policy";
            } else if (frame.status === "flagged" && !m.flagged) {
              updates.flagged = frame.step === "pii" ? "pii" : "policy";
            }
            return { ...m, ...updates };
          });
          const updated: PlaygroundSession = {
            ...target,
            updatedAt: new Date().toISOString(),
            messages: updatedMessages,
          };
          saveSession(updated, sessionIndex);
          return { ...prev, [nextSession.id]: updated };
        });
      };

      const onContent = (frame: ContentFrame) => {
        const delta = frame.choices?.[0]?.delta?.content ?? "";
        const model = frame.model;
        if (!delta && !model) return;
        setSessions((prev) => {
          const target = prev[nextSession.id];
          if (!target) return prev;
          const updatedMessages = target.messages.map((m) =>
            m.id === assistantMsg.id
              ? {
                  ...m,
                  content: delta ? m.content + delta : m.content,
                  model: model ?? m.model,
                }
              : m
          );
          const updated: PlaygroundSession = {
            ...target,
            updatedAt: new Date().toISOString(),
            messages: updatedMessages,
          };
          // Write-through is happening on every chunk — fine for the
          // playground (small sessions, ~hundreds of chunks max).
          saveSession(updated, sessionIndex);
          return { ...prev, [nextSession.id]: updated };
        });
      };

      const onDone = (frame: DoneFrame) => {
        setSessions((prev) => {
          const target = prev[nextSession.id];
          if (!target) return prev;
          const updatedMessages = target.messages.map((m) =>
            m.id === assistantMsg.id && m.governance
              ? {
                  ...m,
                  timing: { totalMs: frame.total_ms },
                  usage: {
                    chunks: frame.usage?.chunks_emitted,
                    tokens: frame.usage?.tokens,
                    finishReason: frame.usage?.finish_reason,
                    costUsd: frame.usage?.cost_usd,
                    costIdr: frame.usage?.cost_idr,
                  },
                  governance: {
                    ...m.governance,
                    reqId: frame.audit_id ?? m.governance.reqId,
                    auditId: frame.audit_id,
                    totalMs: frame.total_ms,
                  },
                }
              : m
          );
          const violations =
            target.stats.violations +
            updatedMessages.filter((m) => m.id === assistantMsg.id && m.flagged)
              .length;
          const updated: PlaygroundSession = {
            ...target,
            updatedAt: new Date().toISOString(),
            messages: updatedMessages,
            stats: {
              ...target.stats,
              tokens:
                target.stats.tokens +
                (frame.usage?.tokens ?? frame.usage?.chunks_emitted ?? 0),
              violations,
            },
          };
          saveSession(updated, sessionIndex);
          return { ...prev, [nextSession.id]: updated };
        });
        setIsStreaming(false);
      };

      const onError = (error: ErrorFrame) => {
        setSessions((prev) => {
          const target = prev[nextSession.id];
          if (!target) return prev;
          const updatedMessages = target.messages.map((m) => {
            if (m.id !== assistantMsg.id) return m;
            return {
              ...m,
              error: { ...error },
              flagged: error.type.includes("policy")
                ? ("policy" as const)
                : error.type.includes("pii")
                  ? ("pii" as const)
                  : (m.flagged ?? null),
            };
          });
          const updated: PlaygroundSession = {
            ...target,
            updatedAt: new Date().toISOString(),
            messages: updatedMessages,
            stats: {
              ...target.stats,
              violations: target.stats.violations + 1,
            },
          };
          saveSession(updated, sessionIndex);
          return { ...prev, [nextSession.id]: updated };
        });
        setIsStreaming(false);
      };

      streamRef.current = streamPlaygroundChat({
        sessionId: activeSessionId,
        request: {
          // Narrowed above — ``nextSession.model`` inherits the same
          // non-null value as ``sessionModel`` (we early-returned when
          // ``session.model`` was nullish, and ``nextSession`` spreads
          // from ``session``).
          model: sessionModel,
          stream: true,
          messages: wireMessages,
          skill_ref: nextSession.skillRef
            ? {
                skill_id: nextSession.skillRef.skillId,
                version: nextSession.skillRef.version,
              }
            : null,
        },
        onGovernance,
        onContent,
        onDone,
        onError,
      });
      try {
        await streamRef.current.done;
      } finally {
        streamRef.current = null;
        setIsStreaming(false);
      }
    },
    [activeSessionId, sessions, sessionIndex, writeSession]
  );

  const abort = useCallback(() => {
    streamRef.current?.abort("user_stop");
  }, []);

  const toggleGovPanel = useCallback(() => {
    setGovPanelOpen((prev) => {
      const next = !prev;
      saveGovPanelOpen(next);
      return next;
    });
  }, []);

  const activeSession = activeSessionId ? sessions[activeSessionId] ?? null : null;
  const boundGovernance = useMemo(() => {
    if (!activeSession) return null;
    if (boundMessageId) {
      return (
        activeSession.messages.find((m) => m.id === boundMessageId) ?? null
      );
    }
    // Default to the most recent assistant message with a trace.
    for (let i = activeSession.messages.length - 1; i >= 0; i--) {
      const m = activeSession.messages[i];
      if (m.role === "assistant" && m.governance) return m;
    }
    return null;
  }, [activeSession, boundMessageId]);

  return {
    sessions,
    sessionIndex,
    activeSessionId,
    activeSession,
    isStreaming,
    hydrated,
    govPanelOpen,
    boundMessageId,
    boundGovernance,
    newSession,
    setActiveSession,
    deleteSession,
    updateSession,
    send,
    abort,
    toggleGovPanel,
    bindMessage,
  };
}

export { STEP_LABELS, STEP_ORDER };
